import type {
	AclEntityMinInfoEntityTypeEnum,
	MetricCategory,
	NotificationCustomFields,
	NotificationGroupedDTO,
	NotificationInfoDTO,
} from "@eu.mdotm.api/sphere";
import { runWithErrorReporting } from "$root/api/error-reporting/report";
import AuthorizationGuard from "$root/components/AuthorizationGuard";
import { IconWallBase } from "$root/components/IconWall";
import { typedUrlForRoute } from "$root/components/PlatformRouter/RoutesDef";
import type { SidebarChildrenProps } from "$root/components/common/Sidebar";
import { VirtualList } from "$root/components/VirtualList";
import { useLocaleFormatters } from "$root/localization/hooks";
import { platformToast } from "$root/notification-system/toast";
import { notificationsGroupMap } from "$root/pages/Notification/map";
import { capitalizeString, customObjectEntriesFn, ToastableError } from "$root/utils";
import { Button, IconButton } from "@mdotm/shadcn/mdotm/components";
import {
	ActionText,
	AutoTooltip,
	CircularProgressBar,
	ClickableArea,
	Icon,
	Text,
	TooltipContent,
	Transition,
} from "@mdotm/shadcn/legacy/components";
import { useAsync } from "@mdotm/utils/promise";
import { generateUniqueDOMId, Switch, toClassName, useUniqueDOMId } from "@mdotm/shadcn/legacy/react-extensions";
import { useUnsafeUpdatedRef, ForEach } from "@mdotm/utils/react-extra";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { noop, stableEmptyObject, switchExpr, unpromisify } from "@mdotm/shadcn/legacy/utils";
import { groupBy, splitAt } from "@mdotm/utils/collections";
import { useInfiniteQuery } from "@tanstack/react-query";
import { isToday, isYesterday } from "date-fns";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { useHistory } from "react-router";
import { match } from "ts-pattern";
import { AutoSentimentBadge } from "../market-view/analysis/SentimentBadge";
import { notificationSettingsRepository } from "../notification-settings/repository";
import { composeOutlookLink, useDriverComposer, usePositionComposer } from "../outlook/useOutlookComposer";
import { portfolioRepository } from "../portfolio/repository";
import { useUserValue } from "../user";
import EmptyBox from "./boxes/EmptyBox";
import { ExecutionDeltaNotification } from "./ExecutionDeltaNotification";

type NotificationType = "market" | "portfolio";

const AssetClassMap = {
	EQUITY: "EQ",
	FIXED_INCOME: "FI",
	COMMODITIES: "CO",
} as Record<string, string>;

// >:(
const REMAP_MICROAC_BE_UNCONSISTENCY = {
	PRECIOUS_METAL: "PRECIOUS_METALS",
	INDUSTRIAL_METAL: "INDUSTRIAL_METALS",
} as Record<string, string>;

// >:(
const REMAP_OUTLOOK_BACKEND_UNCONSISTENCY = {
	CO_PRECIOUS_METAL: "CO_PRECIOUS_METALS",
	CO_INDUSTRIAL_METAL: "CO_INDUSTRIAL_METALS",
} as Record<string, string>;

const areaByEntity = {
	BENCHMARK: "benchmark",
	INVESTMENT: "portfolio",
	MARKET_VIEW: "market view",
	UNIVERSE: "universe",
	GROUP_UNIVERSE: "universe",
	REFERENCE: "targetPortfolio",
} satisfies Record<AclEntityMinInfoEntityTypeEnum, string>;

type NotificationCustomFieldsWithInvestments = NotificationCustomFields & {
	investments?: Array<{
		grantorName: string;
		permissionLevel: string; // null
		sharedEntityName: string;
		sharedEntityType: string;
		sharedEntityUuid: string;
	}>;
};

export function NotificationCenterSidebarContent({
	open,
	position,
	setPosition,
	onClose,
}: SidebarChildrenProps): React.JSX.Element {
	const user = useUserValue();
	const [tabIndex, setTabIndex] = useState(
		useMemo(() => {
			if (user.availableServices.includes("REPORTS") && !user.availableServices.includes("INVESTMENTS")) {
				return 1;
			}
			return 0;
		}, [user.availableServices]),
	);

	const tabTitlesContainerRef = useRef<HTMLDivElement | null>(null);
	const underlineStyle = useMemo<{ left?: number; width?: number }>(() => {
		if (!open) {
			return stableEmptyObject;
		}
		const selectedTab = tabTitlesContainerRef.current?.children[tabIndex] as HTMLButtonElement | undefined;
		if (!selectedTab) {
			return stableEmptyObject;
		}
		return {
			left: selectedTab.offsetLeft,
			width: selectedTab.offsetWidth,
		};
	}, [tabIndex, open]);

	return (
		<div className="h-full w-full flex flex-col bg-white">
			<div className="bg-white border-b border-[var(--palette-N100)] relative z-0">
				<div className="flex justify-between">
					<div className="py-4 px-4 flex items-start space-x-1">
						<Text type="Body/L/Bold" color={themeCSSVars.palette_P600}>
							What&apos;s new
						</Text>
					</div>
					<div className="py-2 px-4 flex items-start space-x-1">
						{setPosition && (
							<Switch
								case={position}
								match={{
									left: () => (
										<IconButton
											unstyled
											onClick={() => setPosition("right")}
											icon="ask-position-rightsmall"
											color={themeCSSVars.palette_N700}
											size={18}
										/>
									),
									right: () => (
										<IconButton
											unstyled
											onClick={() => setPosition("left")}
											icon="ask-positionleftsmall"
											color={themeCSSVars.palette_N700}
											size={18}
										/>
									),
								}}
							/>
						)}
						<IconButton unstyled onClick={() => onClose?.()} icon="Close" color={themeCSSVars.palette_N700} size={18} />
					</div>
				</div>
				<div className="flex items-center space-x-6 ml-4 pb-2" ref={tabTitlesContainerRef}>
					<AuthorizationGuard requiredService="INVESTMENTS">
						{() => (
							<button
								type="button"
								onClick={() => setTabIndex(0)}
								className="flex items-center space-x-2"
								data-qualifier="NotificationCenter/Tab(Portfolio)"
							>
								<Icon
									icon="Portfolio"
									className="**:transition-[fill]"
									size={16}
									color={tabIndex === 0 ? themeCSSVars.palette_N700 : themeCSSVars.palette_N400}
								/>
								<Text
									className="transition-colors"
									type="Body/M/Bold"
									color={tabIndex === 0 ? themeCSSVars.palette_N700 : themeCSSVars.palette_N400}
								>
									Portfolio
								</Text>
							</button>
						)}
					</AuthorizationGuard>
					<AuthorizationGuard requiredService="REPORTS">
						{() => (
							<button
								type="button"
								onClick={() => setTabIndex(1)}
								className="flex items-center space-x-2"
								data-qualifier="NotificationCenter/Tab(Market)"
							>
								<Icon
									icon="News-category-Market-view-aligment"
									className="**:transition-[fill]"
									size={16}
									color={tabIndex === 1 ? themeCSSVars.palette_N700 : themeCSSVars.palette_N400}
								/>
								<Text
									className="transition-colors"
									type="Body/M/Bold"
									color={tabIndex === 1 ? themeCSSVars.palette_N700 : themeCSSVars.palette_N400}
								>
									Market
								</Text>
							</button>
						)}
					</AuthorizationGuard>
				</div>
				<div className="absolute -bottom-px w-full h-px">
					<div
						className={`transition-[left,width] absolute z-0 inset-y-0 bg-[var(--palette-N700)]`}
						style={underlineStyle}
					/>
				</div>
			</div>

			<div className="flex-1 min-h-0 overflow-hidden relative z-0">
				<AuthorizationGuard requiredService="INVESTMENTS">
					{() => (
						<Transition
							in={tabIndex === 0}
							duration={1000}
							className="absolute z-0 inset-0 transition-opacity"
							enterFromClassName="opacity-0"
							enterToClassName="opacity-100"
							exitedClassName="hidden"
						>
							{({ className }) => (
								<div className={toClassName(className)}>
									<NotificationList type="portfolio" key="portfolio" />
								</div>
							)}
						</Transition>
					)}
				</AuthorizationGuard>
				<AuthorizationGuard requiredService="REPORTS">
					{() => (
						<Transition
							in={tabIndex === 1}
							duration={1000}
							className="absolute z-0 inset-0 transition-opacity"
							enterFromClassName={{
								"opacity-0": true,
							}}
							enterToClassName="opacity-100"
							exitedClassName="hidden"
						>
							{({ className }) => (
								<div className={toClassName(className)}>
									<NotificationList type="market" key="market" />
								</div>
							)}
						</Transition>
					)}
				</AuthorizationGuard>
			</div>
		</div>
	);
}

function MarketNotification({
	subNotificationsGroup,
}: {
	subNotificationsGroup: NotificationGroupedDTO["subNotifications"];
}): React.JSX.Element {
	const { t } = useTranslation();
	const id = useUniqueDOMId();
	const { positioningAreaByValue } = usePositionComposer();
	const { composeDrivers } = useDriverComposer();

	const assetClassLabels = t("ASSET_CLASS_LABEL", { returnObjects: true });

	const createMarketRegimeAndPositioning = (positioning: number, type?: string) => {
		if (type === "MARKET_POSITIONING_CHANGES") {
			return positioningAreaByValue[Number(positioning)];
		}

		const positioningMap = {
			1: "Low risk",
			2: "Mid risk",
			3: "High risk",
		};

		return positioningMap[positioning as keyof typeof positioningMap] ?? "-";
	};

	if (!subNotificationsGroup) {
		return <></>;
	}

	const groupNotificationByCategory = groupBy(
		subNotificationsGroup,
		(el) => REMAP_OUTLOOK_BACKEND_UNCONSISTENCY[el.outlookKey ?? "-"] ?? el.outlookKey ?? "-",
	);
	const subList = Object.entries(groupNotificationByCategory)
		.sort()
		.map(([subKey, value]) => {
			const content = value?.map((element, i) => {
				const isPositiveTrend = Number(element.to ?? 0) - Number(element.from ?? 0) > 0;
				return (
					<div className="" key={id + subKey + i}>
						{isPositiveTrend ? (
							element.type === "MARKET_POSITIONING_CHANGES" ? (
								<Icon
									className="mr-1"
									style={{ display: "inline-block", position: "relative", top: 4 }}
									icon="Up1"
									size={18}
									color={themeCSSVars.palette_N800}
								/>
							) : (
								<Icon
									className="mr-1"
									style={{ display: "inline-block", position: "relative", top: 4 }}
									icon="Down1"
									size={18}
									color={themeCSSVars.palette_N800}
								/>
							)
						) : element.type === "MARKET_POSITIONING_CHANGES" ? (
							<Icon
								className="mr-1"
								style={{ display: "inline-block", position: "relative", top: 4 }}
								icon="Down1"
								size={18}
								color={themeCSSVars.palette_N800}
							/>
						) : (
							<Icon
								className="mr-1"
								style={{ display: "inline-block", position: "relative", top: 4 }}
								icon="Up1"
								size={18}
								color={themeCSSVars.palette_N800}
							/>
						)}

						{element.from && element.to && (
							<Trans
								i18nKey={`NEW_NOTIFICATIONS.NOTIFICATIONS.${
									element.type === "MARKET_POSITIONING_CHANGES" ? "MARKET_POSITIONING_CHANGES" : "MARKET_REGIME_CHANGES"
								}`}
								components={{
									sentiment: <AutoSentimentBadge size="x-small" />,
								}}
								className={`text-[12px] text-[var(--palette-N500)]`}
								values={{
									from: createMarketRegimeAndPositioning(Number(element.from), element.type),
									to: createMarketRegimeAndPositioning(Number(element.to), element.type),
									driver: composeDrivers(
										createMarketRegimeAndPositioning(Number(element.to), element.type),
										element.driverLabel,
									)
										.map((x) => x.label)
										.join(", ")
										.toString(),
								}}
							/>
						)}
					</div>
				);
			});

			return (
				<div key={subKey} className="mb-1">
					<Text type="Body/M/Bold" className="mb-1" as="p">
						{assetClassLabels[subKey as keyof typeof assetClassLabels] ?? subKey}
					</Text>
					{content}
				</div>
			);
		});

	return <>{subList}</>;
}

function PortfolioNotification({
	subNotificationsGroup,
}: {
	subNotificationsGroup: NotificationGroupedDTO["subNotifications"];
}): React.JSX.Element {
	const { t } = useTranslation();
	const id = useUniqueDOMId();
	const notificationTransObj = t("NEW_NOTIFICATIONS", { returnObjects: true });
	const metricTranslation = t(`MONITORING_METRICS`, { returnObjects: true });
	const varTranslation = t("VAR", { returnObjects: true });

	const createMetricSubType = useCallback(
		(category: MetricCategory, fields: NotificationCustomFields) =>
			match(category)
				.with("RISK", () => varTranslation[fields.entity as keyof typeof varTranslation] ?? fields.entity ?? "")
				.otherwise(() => (fields.entity && fields.relation ? `${fields.entity}` : "")),
		[varTranslation],
	);

	if (!subNotificationsGroup) {
		return <></>;
	}

	const { investmentName, key } = subNotificationsGroup[0];

	const groupNotificationByCategory = groupBy(subNotificationsGroup, (el) => {
		if (el.customFields !== undefined && Object.keys(el.customFields).length > 0) {
			return el.value === "CONSTRAINT" ? "NOTIFICATION_PORTFOLIO_ALERT" : "NOTIFICATION_PORTFOLIO_WARNING";
		}

		return (
			notificationsGroupMap[(el.key ?? "default") as keyof typeof notificationsGroupMap] ??
			notificationsGroupMap["default"]
		);
	});
	const subList = Object.entries(groupNotificationByCategory)
		.sort()
		.map(([subKey, value]) => {
			const content = value?.map((element, i) => {
				const { customFields } = element;

				if (customFields === undefined || Object.values(customFields).length === 0) {
					const label =
						notificationTransObj.NOTIFICATIONS[element.key as keyof typeof notificationTransObj.NOTIFICATIONS] ??
						element.key ??
						"-";

					return (
						<div className="flex items-start space-x-1" key={id + subKey + i}>
							{/* <Icon icon="previous-position" style={{ marginTop: 2 }} size={12} /> */}
							<Text type="Body/M/Book" as="p">
								{label}
							</Text>
						</div>
					);
				}

				return (
					<div key={id + subKey + i} className="mb-1.5 pl-2">
						<Text type="Body/M/Bold" className="block">
							{customFields.category ? metricTranslation[customFields.category] : "-"}
						</Text>
						{customFields.category && (
							<Text type="Body/M/Book">{createMetricSubType(customFields.category, customFields)}</Text>
						)}
					</div>
				);
			});

			return (
				<div key={subKey} className="mb-1">
					<Text type="Body/M/Bold" className="mb-1" as="p">
						{notificationTransObj.NOTIFICATION_GROUPS[
							subKey as keyof typeof notificationTransObj.NOTIFICATION_GROUPS
						] ?? subKey}
					</Text>
					{content}
				</div>
			);
		});

	return (
		<>
			<Text type="Body/L/Bold" className="mb-1.5" as="p">
				{key === "BulkReport" ? "Download bulk report" : investmentName}
			</Text>
			{subList}
		</>
	);
}

function DownloadNotification(props: { subNotification: NotificationInfoDTO }) {
	const { subNotification } = props;

	const { t } = useTranslation();

	const onDownloadGeneratedPdfs = useCallback(
		async (zipId?: string, userName?: string) => {
			await runWithErrorReporting(
				async () => {
					try {
						if (!zipId) {
							throw new Error("missing zip file identifier");
						}
						await portfolioRepository.downloadReportZip(zipId, {
							prefix: userName,
						});
					} catch (error) {
						throw new ToastableError(t("SOMETHING_WENT_WRONG"), { cause: error, icon: "Portfolio" });
					}
				},
				{
					area: "portfolio",
					attemptedOperation: {
						message: "unable to download the generated zip",
						payload: JSON.stringify({ zipId, userName }),
					},
				},
			);
		},
		[t],
	);

	function generateDescription(portfolios: string[]) {
		if (portfolios.length <= 3) {
			return `Your report for “${portfolios.join(", ")}” is ready`;
		}

		const [shown, remaining] = splitAt(portfolios, 3);
		return `Your report for “${shown.join(", ")} and ${remaining.length}” is ready`;
	}

	const { customFields } = subNotification;

	return (
		<div>
			<Text type="Body/L/Bold" className="mb-1.5" as="p">{`You report is ready `}</Text>

			{customFields?.successList && (
				<Text type="Body/M/Book" as="p">
					{generateDescription(customFields?.successList)}
				</Text>
			)}
			<ActionText
				className={{
					"mb-2": customFields?.errorList && customFields?.errorList.length > 0,
				}}
				onClickAsync={() => onDownloadGeneratedPdfs(customFields?.zipId, customFields?.userName)}
				disabled={customFields?.isExpired}
				data-qualifier="NotificationCenter/Item/Type(BULK_REPORT)/DownloadButton"
			>
				Download
			</ActionText>
			{customFields?.errorList && (
				<Text type="Body/M/Book" className="mb-1" as="p">
					{`Report could not be generated for “${customFields.errorList.join(", ")}”`}
				</Text>
			)}
		</div>
	);
}

function AccessControlListNotification(props: { subNotification: NotificationInfoDTO }) {
	const { subNotification } = props;
	const { customFields } = subNotification;

	const grantorName = customFields?.grantorName;
	//TODO(workaround: replace with ENUM)
	const permissionLevel = customFields?.permissionLevel;

	return (
		<div>
			<Text type="Body/L/Bold" className="mb-1.5" as="p">
				Permission updated
			</Text>
			<Text type="Body/M/Book" as="p">
				{grantorName ?? "User"} has assigned you the {permissionLevel ? capitalizeString(permissionLevel) : "-"} role
				for:
			</Text>
			<ActionText
				href={generateEntityLink(
					customFields?.sharedEntityType as AclEntityMinInfoEntityTypeEnum,
					customFields?.sharedEntityUuid,
				)}
				disabled={customFields?.sharedEntityUuid === undefined}
			>
				{customFields?.sharedEntityName}
			</ActionText>
		</div>
	);
}

function BulkAccessControlListNotification(props: {
	subNotification: NotificationInfoDTO;
	updateMode: "edit" | "remove";
}) {
	const { customFields } = props.subNotification;

	const castedCustomFields = customFields as NotificationCustomFieldsWithInvestments | undefined;

	const sharedEntities = castedCustomFields?.investments ?? [];
	const grantorName = customFields?.grantorName;
	//TODO(workaround: replace with ENUM)
	const permissionLevel = customFields?.permissionLevel;
	const [shown, remaining] = splitAt(sharedEntities, 3);

	return (
		<div>
			<Text type="Body/L/Bold" className="mb-1.5" as="p">
				Permission updated
			</Text>
			<Text type="Body/M/Book" as="p">
				{props.updateMode === "edit" ? (
					<>
						{grantorName ?? "User"} has assigned you the {permissionLevel ?? "-"} role for:
					</>
				) : (
					<>{grantorName ?? "User"} has removed your access to:</>
				)}
			</Text>
			<AutoTooltip
				trigger={(tooltipProps) => {
					return (
						<>
							<ForEach collection={shown}>
								{(item) =>
									props.updateMode === "remove" ? (
										<Text type="Body/M/Medium">{item.sharedEntityName ?? "-"}</Text>
									) : (
										<>
											<ActionText
												href={generateEntityLink(
													item.sharedEntityType as AclEntityMinInfoEntityTypeEnum,
													item.sharedEntityUuid,
												)}
												disabled={item.sharedEntityUuid === undefined}
											>
												{item.sharedEntityName ?? "-"}
											</ActionText>
											&nbsp;
										</>
									)
								}
							</ForEach>

							{remaining.length > 0 ? (
								<Text type="Body/M/Link" {...tooltipProps} className="underline underline-offset-[3px]">
									and ${remaining.length} more
								</Text>
							) : (
								""
							)}
						</>
					);
				}}
			>
				<TooltipContent>
					<ForEach collection={remaining}>{(item) => <li>{item.sharedEntityName}</li>}</ForEach>
				</TooltipContent>
			</AutoTooltip>
		</div>
	);
}

function EditEntityNotification(props: { subNotification: NotificationInfoDTO }) {
	const { subNotification } = props;
	const { customFields } = subNotification;
	return (
		<div>
			<Text type="Body/L/Bold" className="mb-1.5" as="p">
				Edit
			</Text>
			<Text type="Body/M/Book" as="p">
				{`${customFields?.grantorName} has edited`}
			</Text>
			<ActionText
				href={generateEntityLink(
					customFields?.sharedEntityType as AclEntityMinInfoEntityTypeEnum,
					customFields?.sharedEntityUuid,
				)}
				disabled={customFields?.sharedEntityUuid === undefined}
			>
				{customFields?.sharedEntityName}
			</ActionText>
			&nbsp;
			{customFields?.sharedEntityType
				? areaByEntity[customFields?.sharedEntityType as AclEntityMinInfoEntityTypeEnum]
				: ""}
		</div>
	);
}

function DeleteEntityNotification(props: { subNotification: NotificationInfoDTO }) {
	const { subNotification } = props;
	const { customFields } = subNotification;

	return (
		<div>
			<Text type="Body/L/Bold" className="mb-1.5" as="p">
				Delete
			</Text>
			<Text type="Body/M/Book" as="p">
				{`${customFields?.grantorName} has deleted`}
			</Text>
			<p className="font-semibold underline">{customFields?.sharedEntityName}</p>
			&nbsp;
			{customFields?.sharedEntityType
				? areaByEntity[customFields?.sharedEntityType as AclEntityMinInfoEntityTypeEnum]
				: ""}
		</div>
	);
}

function RemovedEntityNotification(props: { subNotification: NotificationInfoDTO }) {
	const { subNotification } = props;
	const { customFields } = subNotification;

	return (
		<div>
			<Text type="Body/L/Bold" className="mb-1.5" as="p">
				Permission Update
			</Text>
			<Text type="Body/M/Book" as="p">
				{`${customFields?.grantorName ?? "User"} has removed your access to:`}
			</Text>
			<Text type="Body/M/Medium">{customFields?.sharedEntityName ?? "-"}</Text>
		</div>
	);
}

type ListProps = {
	currentPage: number;
	pagePool: number;
	results:
		| {
				id: string;
				subNotificationsIds: string[] | undefined;
				timestamp: string;
				read: boolean;
				type: string;
				investmentUUID: string;
				urlTo: string;
				content: React.JSX.Element;
				page?: number;
				key?: string;
		  }[]
		| undefined;
};

export function NotificationList({ type }: { type: NotificationType }): React.JSX.Element {
	const history = useHistory();
	const { ref, inView } = useInView();

	const { formatDate: _formatDate } = useLocaleFormatters();

	const { data, fetchNextPage, hasNextPage, refetch } = useInfiniteQuery<ListProps>({
		queryKey:
			type === "portfolio"
				? notificationSettingsRepository.retrieveAllUserPortfolioNotificationsFromPool.fullQueryKey
				: notificationSettingsRepository.retrieveAllUserMarketNotificationsFromPool.fullQueryKey,
		queryFn: async ({ pageParam = 0 }) => {
			const isPortfolio = type === "portfolio";
			const notifications = isPortfolio
				? await notificationSettingsRepository.retrieveAllUserPortfolioNotificationsFromPool.fetchQuery(pageParam)
				: await notificationSettingsRepository.retrieveAllUserMarketNotificationsFromPool.fetchQuery(pageParam);

			const groupSubNotification = notifications.elements?.reduce<
				Array<{
					id: string;
					subNotificationsIds: string[] | undefined;
					timestamp: string;
					read: boolean;
					type: string;
					investmentUUID: string;
					urlTo: string;
					content: React.JSX.Element;
				}>
			>((acc, pool) => {
				const groupedNotificationByKey = groupBy(pool.subNotifications ?? [], (el) => {
					if (el.key === "ExecutionDelta") {
						return el.key;
					}

					if (el.investmentName && el.investmentName !== "null") {
						return el.investmentName;
					}

					if (el.outlookKey && el.outlookKey !== "null") {
						return el.outlookKey;
					}

					if (
						el.key &&
						el.key !== "null" &&
						(el.key === "BulkReport" ||
							el.key === "SharedEntity" ||
							el.key === "SharedEntities" ||
							el.key === "RemovedEntities" ||
							el.key === "ModifiedEntities" ||
							el.key === "ModifiedEntity" ||
							el.key === "DeletedEntity" ||
							el.key === "RemovedEntity")
					) {
						return el.key;
					}

					return "-";
				});

				const notificationCollection = customObjectEntriesFn(groupedNotificationByKey).flatMap(([key, value]) => {
					if (key === "BulkReport") {
						const mappedBulkReportNotification =
							value?.map((notification) => ({
								id: generateUniqueDOMId(),
								subNotificationsIds: notification.id ? [notification.id] : [],
								timestamp: pool.data ?? "",
								read: notification.read ?? true,
								type: pool.type ?? "",
								urlTo: "",
								investmentUUID: notification?.investmentUUID ?? "",
								content: <DownloadNotification subNotification={notification} />,
								page: pageParam,
								key,
							})) ?? [];

						return mappedBulkReportNotification;
					}

					if (key === "ExecutionDelta") {
						const mappedExecutionDeltaNotification =
							value?.map((notification) => ({
								id: generateUniqueDOMId(),
								subNotificationsIds: notification.id ? [notification.id] : [],
								timestamp: pool.data ?? "",
								read: notification.read ?? true,
								type: pool.type ?? "",
								urlTo: "",
								investmentUUID: notification?.investmentUUID ?? "",
								content: <ExecutionDeltaNotification subNotification={notification} />,
								page: pageParam,
								key,
							})) ?? [];

						return mappedExecutionDeltaNotification;
					}

					if (key === "SharedEntity") {
						const mappedBulkSharingNotification =
							value?.map((notification) => ({
								id: generateUniqueDOMId(),
								subNotificationsIds: notification.id ? [notification.id] : [],
								timestamp: pool.data ?? "",
								read: notification.read ?? true,
								type: pool.type ?? "",
								urlTo: "",
								investmentUUID: notification?.investmentUUID ?? "",
								content: <AccessControlListNotification subNotification={notification} />,
								page: pageParam,
								key,
							})) ?? [];

						return mappedBulkSharingNotification;
					}

					if (key === "SharedEntities") {
						const mappedBulkSharingNotification =
							value?.map((notification) => ({
								id: generateUniqueDOMId(),
								subNotificationsIds: notification.id ? [notification.id] : [],
								timestamp: pool.data ?? "",
								read: notification.read ?? true,
								type: pool.type ?? "",
								urlTo: "",
								investmentUUID: notification?.investmentUUID ?? "",
								content: <BulkAccessControlListNotification subNotification={notification} updateMode="edit" />,
								page: pageParam,
								key,
							})) ?? [];

						return mappedBulkSharingNotification;
					}

					if (key === "RemovedEntities") {
						const mappedBulkSharingNotification =
							value?.map((notification) => ({
								id: generateUniqueDOMId(),
								subNotificationsIds: notification.id ? [notification.id] : [],
								timestamp: pool.data ?? "",
								read: notification.read ?? true,
								type: pool.type ?? "",
								urlTo: "",
								investmentUUID: notification?.investmentUUID ?? "",
								content: <BulkAccessControlListNotification subNotification={notification} updateMode="remove" />,
								page: pageParam,
								key,
							})) ?? [];

						return mappedBulkSharingNotification;
					}

					if (key === "ModifiedEntities") {
						const mappedBulkSharingNotification =
							value?.map((notification) => ({
								id: generateUniqueDOMId(),
								subNotificationsIds: notification.id ? [notification.id] : [],
								timestamp: pool.data ?? "",
								read: notification.read ?? true,
								type: pool.type ?? "",
								urlTo: "",
								investmentUUID: notification?.investmentUUID ?? "",
								content: <BulkAccessControlListNotification subNotification={notification} updateMode="edit" />,
								page: pageParam,
								key,
							})) ?? [];

						return mappedBulkSharingNotification;
					}

					if (key === "ModifiedEntity" || key === "ModifiedEntities") {
						const mappedBulkSharingNotification =
							value?.map((notification) => ({
								id: generateUniqueDOMId(),
								subNotificationsIds: notification.id ? [notification.id] : [],
								timestamp: pool.data ?? "",
								read: notification.read ?? true,
								type: pool.type ?? "",
								urlTo: "",
								investmentUUID: notification?.investmentUUID ?? "",
								content: <EditEntityNotification subNotification={notification} />,
								page: pageParam,
								key,
							})) ?? [];

						return mappedBulkSharingNotification;
					}

					if (key === "DeletedEntity") {
						const mappedBulkSharingNotification =
							value?.map((notification) => ({
								id: generateUniqueDOMId(),
								subNotificationsIds: notification.id ? [notification.id] : [],
								timestamp: pool.data ?? "",
								read: notification.read ?? true,
								type: pool.type ?? "",
								urlTo: "",
								investmentUUID: notification?.investmentUUID ?? "",
								content: <DeleteEntityNotification subNotification={notification} />,
								page: pageParam,
								key,
							})) ?? [];

						return mappedBulkSharingNotification;
					}

					if (key === "RemovedEntity") {
						const mappedBulkSharingNotification =
							value?.map((notification) => ({
								id: generateUniqueDOMId(),
								subNotificationsIds: notification.id ? [notification.id] : [],
								timestamp: pool.data ?? "",
								read: notification.read ?? true,
								type: pool.type ?? "",
								urlTo: "",
								investmentUUID: notification?.investmentUUID ?? "",
								content: <RemovedEntityNotification subNotification={notification} />,
								page: pageParam,
								key,
							})) ?? [];

						return mappedBulkSharingNotification;
					}

					if (type === "market") {
						const url =
							value && value.length > 0
								? value?.at(0)?.outlookKey === "EQ" ||
									value?.at(0)?.outlookKey === "FI" ||
									value?.at(0)?.outlookKey === "CO"
									? "/dashboard"
									: composeOutlookLink(
											AssetClassMap[value[0].assetClass ?? ""] ?? "",
											value[0].geography ?? "",
											REMAP_MICROAC_BE_UNCONSISTENCY[value[0].microAc ?? "-"] ?? value[0].microAc ?? "",
											value[0].geography ?? "",
										)
								: "/dashboard";

						return [
							{
								id: generateUniqueDOMId(),
								subNotificationsIds: pool.read ? [] : (value?.map((el) => el.id!) ?? []),
								timestamp: pool.data ?? "",
								read: value?.every((x) => x.read) ?? false,
								type: pool.type ?? "",
								urlTo:
									(REMAP_MICROAC_BE_UNCONSISTENCY[value?.[0]?.microAc ?? "-"] ?? value?.[0]?.microAc ?? "") === "ALL"
										? "/dashboard"
										: url,
								investmentUUID: value?.[0].investmentUUID ?? "",
								content: <MarketNotification subNotificationsGroup={value} />,
								page: pageParam,
								key,
							},
						];
					}

					return [
						{
							id: generateUniqueDOMId(),
							subNotificationsIds: pool.read ? [] : (value?.map((el) => el.id!) ?? []),
							timestamp: pool.data ?? "",
							read: value?.every((x) => x.read) ?? false,
							type: pool.type ?? "",
							urlTo: `/portfolio_details/${value?.[0].investmentUUID ?? ""}`,
							investmentUUID: value?.[0].investmentUUID ?? "",
							content: <PortfolioNotification subNotificationsGroup={value} />,
							page: pageParam,
							key,
						},
					];
				});
				return [...acc, ...notificationCollection];
			}, []);

			return {
				currentPage: pageParam,
				pagePool: notifications.totalPool ?? 0,
				results: groupSubNotification,
			};
		},
		getPreviousPageParam: ({ currentPage }) => currentPage - 1,
		getNextPageParam: ({ currentPage, pagePool }) => (pagePool === currentPage + 1 ? undefined : currentPage + 1),
	});

	const formatDate = useCallback(
		(date: string | number): string => {
			const dateInstance = new Date(date);
			return isToday(dateInstance) ? "Today" : isYesterday(dateInstance) ? "Yesterday" : _formatDate(date);
		},
		[_formatDate],
	);

	const groupedByDate = useMemo(
		() =>
			customObjectEntriesFn(
				(data?.pages &&
					groupBy(
						data.pages.flatMap((page) => page.results),
						(x) => formatDate(x?.timestamp ?? ""),
					)) ??
					{},
			),
		[data?.pages, formatDate],
	);

	const refs = useUnsafeUpdatedRef({ fetchNextPage });
	useEffect(() => {
		noop(data); // track
		if (inView) {
			unpromisify(async () => {
				await refs.current.fetchNextPage();
			})();
		}
	}, [inView, refs, data]);

	const [notificationAction] = useAsync<
		void,
		[
			{
				notificationType: NotificationGroupedDTO["type"];
				investmentUUID?: string;
				redirect?: boolean;
				page?: number;
				ids?: string[];
				type: "markAsRead" | "delete";
			},
		]
	>(async (param) => {
		try {
			if (param.type === "markAsRead") {
				if (param.ids === undefined) {
					throw new Error("unable to find valid ids");
				}
				await notificationSettingsRepository.changeReadNotification1(param.ids);
				return;
			}

			if (param.ids === undefined) {
				throw new Error("unable to find a valid id");
			}
			if (param.type === "delete") {
				await notificationSettingsRepository.deleteNotifications(param.ids);
			}
		} catch (err) {
			platformToast({
				children: "Something went wrong please, try later",
				icon: "Icon-full-error",
				severity: "error",
			});
			throw err;
		}
	});

	if (groupedByDate.length === 0) {
		return (
			<div className="h-full">
				<IconWallBase opaque>
					<div className="grid justify-center items-center gap-1">
						<div className="mx-auto">
							<EmptyBox />
						</div>
						<Text className="transition-colors" type="Body/L/Bold" color={themeCSSVars.palette_N700}>
							You don&apos;t have new notifications
						</Text>
					</div>
				</IconWallBase>
			</div>
		);
	}

	return (
		<div className="flex flex-col h-full w-full min-h-0">
			<VirtualList items={groupedByDate}>
				{({ item: [date, items] }) => (
					<>
						<div className={`px-4 py-4 border-b border-b-[var(--palette-N200)] bg-[var(--palette-N20)]`}>
							<Text color={themeCSSVars.palette_N500} type="Body/M/Bold" className="uppercase">
								{date}
							</Text>
						</div>
						{items?.map(
							(item) =>
								item && (
									<Fragment key={item.id}>
										<div
											className={toClassName({
												[`relative z-0 px-4 bg-white  border-b border-b-[var(--palette-N100)] pt-2 pb-4 [&:hover>div:nth-child(2)>div]:bg-white [&:hover>div:nth-child(2)>div]:border [&:hover>div:nth-child(2)>div>div>button]:opacity-100!`]: true,
												"opacity-75": item.read,
											})}
											data-qualifier={`NotificationCenter/Item/Type(${item.type})`}
										>
											{item.key === "BulkReport" ||
											item.key === "SharedEntity" ||
											item.key === "ModifiedEntity" ||
											item.key === "DeletedEntity" ||
											item.key === "RemovedEntity" ||
											item.key === "SharedEntities" ||
											item.key === "RemovedEntities" ||
											item.key === "ModifiedEntities" ||
											item.key === "ExecutionDelta" ? (
												<div>{item.content}</div>
											) : (
												<ClickableArea
													onClick={(e) => {
														e.stopPropagation();
														if (item.read === false) {
															unpromisify(() =>
																notificationAction({
																	ids: item.subNotificationsIds,
																	notificationType: item.type,
																	investmentUUID: item.investmentUUID,
																	redirect: true,
																	page: item.page,
																	type: "markAsRead",
																}),
															)();
														}

														history.push(item.urlTo);
													}}
												>
													<div>{item.content}</div>
												</ClickableArea>
											)}
											<div className="absolute top-3 right-0 z-50">
												<div className="hover:shadow-sm p-1.5 rounded-sm transition-all duration-100 ">
													<div className="flex gap-2 items-center overflow-hidden">
														<Button
															unstyled
															className={toClassName({
																"opacity-0 transition-all duration-200 px-2 py-1 rounded-sm": true,
																[`hover:bg-[color:var(--palette-N50)]`]: true,
																hidden: item.read,
															})}
															onClick={() =>
																unpromisify(() =>
																	notificationAction({
																		ids: item.subNotificationsIds,
																		notificationType: item.type,
																		investmentUUID: item.investmentUUID,
																		page: item.page,
																		type: "markAsRead",
																	}),
																)()
															}
															data-qualifier={`NotificationCenter/Item/Type(${item.type})/Action(MarkAsRead)`}
														>
															<Text as="span" type="Body/M/Medium">
																Mark as read
															</Text>
														</Button>
														<Button
															unstyled
															className={toClassName({
																"opacity-0 transition-all duration-200 px-2 py-1 rounded-sm": true,
																[`hover:bg-[color:var(--palette-N50)]`]: true,
															})}
															onClick={() =>
																unpromisify(() =>
																	notificationAction({
																		notificationType: item.type,
																		investmentUUID: item.investmentUUID,
																		page: item.page,
																		type: "delete",
																		ids: item.subNotificationsIds,
																	}),
																)()
															}
															data-qualifier={`NotificationCenter/Item/Type(${item.type})/Item/Action(Delete)`}
														>
															<Text as="span" type="Body/M/Medium">
																Delete
															</Text>
														</Button>
														<div
															className={toClassName({
																[`h-[8px] w-[8px] rounded-full block bg-[var(--palette-S500)] aria-hidden:hidden`]: true,
																hidden: item.read,
															})}
														/>
													</div>
												</div>
											</div>
										</div>

										{hasNextPage && item.id === data?.pages.at(-1)?.results?.at(-1)?.id && (
											<div ref={ref} className="flex items-center justify-center py-2">
												<CircularProgressBar value="indeterminate" style={{ width: 16 }} />
											</div>
										)}
									</Fragment>
								),
						)}
					</>
				)}
			</VirtualList>
			<div className="text-right py-2.5 px-4 shadow-t">
				<ActionText
					onClickAsync={async () => {
						await notificationSettingsRepository.readAllNotifications();
						await refetch();
					}}
					color={themeCSSVars.palette_P500}
					data-qualifier="NotificationCenter/Action(MarkAllAsRead)"
				>
					Mark all as read
				</ActionText>
			</div>
		</div>
	);
}

/** @internal — exported for tests */
export function generateEntityLink(entity: AclEntityMinInfoEntityTypeEnum, uuid?: string): string {
	return switchExpr(entity, {
		INVESTMENT: () => typedUrlForRoute("PortfolioDetails", { portfolioUid: uuid ?? "" }),
		BENCHMARK: () => typedUrlForRoute("LegacyBenchmark", { uuid: uuid ?? "" }),
		MARKET_VIEW: () => typedUrlForRoute("MarketViewWorkSpace/View", { uuid: uuid ?? "" }),
		UNIVERSE: () => typedUrlForRoute("UniverseDetails", { universeUuid: uuid ?? "" }),
		GROUP_UNIVERSE: () => typedUrlForRoute("UniverseWithGroupsDetails", { universeUuid: uuid ?? "" }),
		REFERENCE: () => typedUrlForRoute("ReferenceDetails", { uuid: uuid ?? "" }),
	});
}
